import { useEffect, useState } from 'react';

type Rule = { key: string; enabled: number; threshold: number; severity: string; label: string };
type Ev = { key: string; severity: string; message: string; value: number; at: number };
type E911 = { start_time: string; calling_number: string; user_email: string; location: string; site: string };

const dt = (e: number) => (e ? new Date(e).toLocaleString() : '');
export default function CdrAlerts() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [rec, setRec] = useState(''); const [room, setRoom] = useState(''); const [e911rec, setE911rec] = useState('');
  const [events, setEvents] = useState<Ev[]>([]);
  const [e911, setE911] = useState<E911[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      const a = await (await fetch('/cdr/api/alerts', { credentials: 'include' })).json();
      if (a.error) { setErr(a.error); return; }
      setRules(a.rules || []); setRec(a.recipients || ''); setRoom(a.webex_room || '');
      setEvents(((await (await fetch('/cdr/api/alerts/events', { credentials: 'include' })).json()).events) || []);
      const e = await (await fetch('/cdr/api/e911?limit=200', { credentials: 'include' })).json();
      setE911(e.rows || []); setE911rec(e.recipients || '');
    } catch (e: any) { setErr(String(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function saveCfg(extra: any = {}) {
    setMsg(null);
    const r = await fetch('/cdr/api/alerts/config', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ recipients: rec, webex_room: room, e911_recipients: e911rec, ...extra }) });
    setMsg(r.ok ? 'saved' : 'error (admin only)'); load();
  }
  async function runNow() { setMsg('running…'); const r = await fetch('/cdr/api/alerts/run', { method: 'POST', credentials: 'include' }); const j = await r.json(); setMsg(r.ok ? `checked — ${j.fired} fired` : 'error (admin only)'); load(); }

  return (
    <div className="space-y-4">
      {err && <div className="text-xs text-red-500">Sign in to the CDR portal to view ({err}).</div>}

      <div className="card p-3 space-y-2">
        <div className="text-sm font-semibold">Alert notifications</div>
        <div className="flex gap-2 flex-wrap items-center text-xs">
          <span>Email</span><input value={rec} onChange={e => setRec(e.target.value)} placeholder="alerts@santamonica.gov, ..." className="border rounded px-2 py-1 bg-transparent w-64" />
          <span>911 email</span><input value={e911rec} onChange={e => setE911rec(e.target.value)} placeholder="security@…" className="border rounded px-2 py-1 bg-transparent w-56" />
          <span>Webex roomId</span><input value={room} onChange={e => setRoom(e.target.value)} placeholder="optional" className="border rounded px-2 py-1 bg-transparent w-56" />
          <button onClick={() => saveCfg()} className="px-3 py-1 rounded bg-tnh-accent text-white">Save</button>
          <button onClick={runNow} className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800">Run check now</button>
          {msg && <span className="text-gray-500">{msg}</span>}
        </div>
      </div>

      <div className="card p-3">
        <div className="text-sm font-semibold mb-2">Rules</div>
        <table className="w-full text-xs">
          <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Rule</th><th className="text-left px-2 py-1">Severity</th><th className="text-right px-2 py-1">Threshold</th><th className="px-2 py-1">On</th></tr></thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.key} className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-2 py-1">{r.label}</td>
                <td className="px-2 py-1">{r.severity}</td>
                <td className="px-2 py-1 text-right"><input defaultValue={r.threshold} onBlur={e => saveCfg({ key: r.key, threshold: Number(e.target.value) })} className="border rounded px-1 py-0.5 w-24 text-right bg-transparent" /></td>
                <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!r.enabled} onChange={e => saveCfg({ key: r.key, enabled: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-3">
        <div className="text-sm font-semibold mb-2">Recent alerts</div>
        <table className="w-full text-xs">
          <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">When</th><th className="text-left px-2 py-1">Severity</th><th className="text-left px-2 py-1">Message</th></tr></thead>
          <tbody>{events.length ? events.map((e, i) => (<tr key={i} className="border-t border-gray-100 dark:border-gray-800"><td className="px-2 py-1">{dt(e.at)}</td><td className="px-2 py-1">{e.severity}</td><td className="px-2 py-1">{e.message}</td></tr>)) : <tr><td colSpan={3} className="px-2 py-2 text-gray-400">No alerts fired.</td></tr>}</tbody>
        </table>
      </div>

      <div className="card p-3">
        <div className="text-sm font-semibold mb-2">911 Call Log <span className="text-gray-500 font-normal">— Kari's Law / RAY BAUM compliance</span></div>
        <table className="w-full text-xs">
          <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Time</th><th className="text-left px-2 py-1">Caller</th><th className="text-left px-2 py-1">User</th><th className="text-left px-2 py-1">Location</th></tr></thead>
          <tbody>{e911.length ? e911.map((r, i) => (<tr key={i} className="border-t border-gray-100 dark:border-gray-800"><td className="px-2 py-1">{r.start_time}</td><td className="px-2 py-1 font-mono">{r.calling_number}</td><td className="px-2 py-1">{r.user_email}</td><td className="px-2 py-1">{r.location} {r.site}</td></tr>)) : <tr><td colSpan={4} className="px-2 py-2 text-gray-400">No 911 calls on record.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}
